import React from 'react';
import { Check, RefreshCcw, Save } from 'lucide-react';

import PriceDistributionView from '../PriceDistributionView';
import { formatConfidencePercent } from '../../utils/formatters';

const conditionBadgeClass = (condition) => {
  if (condition === 'New') return 'bg-green-100 text-green-700';
  if (condition === 'Damaged' || condition === 'For Parts') return 'bg-red-100 text-red-700';
  if (condition === 'Unknown') return 'bg-slate-100 text-slate-500';
  return 'bg-yellow-100 text-yellow-700';
};

const sourceBadge = (priceSource) => {
  if (priceSource === 'ebay_rag') return { label: 'eBay RAG', className: 'bg-blue-100 text-blue-700' };
  if (priceSource === 'image') return { label: 'Image', className: 'bg-amber-100 text-amber-700' };
  return { label: 'Model Knowledge', className: 'bg-slate-100 text-slate-600' };
};

const HistoryGroupDesktopTable = ({
  group,
  originalItemsById,
  updatingId,
  updatingNameId,
  onOpenItemImage,
  onManualNameChange,
  onUpdateItemName,
  onPriceChange,
  onUpdatePrice,
}) => {
  return (
    <div className="hidden sm:block overflow-x-auto">
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="bg-white border-b border-slate-100">
            <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">AI Name</th>
            <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Manual Name</th>
            <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Est. Value</th>
            <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Confidence</th>
            <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Actual Price</th>
            <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Condition</th>
            <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Price Source</th>
          </tr>
        </thead>
        <tbody>
          {group.items.map((item) => {
            const originalItem = originalItemsById[item.id];
            const isDirty = originalItem && parseFloat(originalItem.actual_price) !== parseFloat(item.actual_price);
            const isUpdating = updatingId === item.id;
            const isNameDirty = originalItem && (originalItem.manual_name || '') !== (item.manual_name || '');
            const isNameUpdating = updatingNameId === item.id;
            const condition = item?.condition || 'Unknown';
            const source = sourceBadge(item?.price_source || 'model_knowledge');

            return (
              <tr key={item.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors last:border-0">
                <td className="p-4 text-sm font-medium text-slate-700">
                  <button onClick={() => onOpenItemImage(item)} className="hover:text-blue-600 hover:underline transition-colors text-left">
                    {item.item_name}
                  </button>
                </td>
                <td className="p-4 text-sm">
                  <div className="relative inline-flex items-center">
                    <input
                      type="text"
                      placeholder="Custom name..."
                      value={item.manual_name || ''}
                      onChange={(e) => onManualNameChange(item.id, e.target.value)}
                      className={`w-36 pr-8 py-1 px-2 border rounded text-sm focus:outline-none focus:ring-1 transition-all ${
                        isNameDirty ? 'border-blue-300 focus:ring-blue-500 bg-blue-50/30' : 'border-slate-200 focus:ring-green-500'
                      }`}
                    />
                    <button
                      onClick={() => onUpdateItemName(item.id, item.manual_name || '')}
                      disabled={!isNameDirty || isNameUpdating}
                      className={`absolute right-1 p-1 rounded transition-all ${
                        isNameUpdating ? 'text-slate-400' : (isNameDirty ? 'text-blue-600 hover:bg-blue-100' : 'text-green-500 cursor-default')
                      }`}
                      title={isNameDirty ? 'Save Name' : 'Name Saved'}
                    >
                      {isNameUpdating ? <RefreshCcw size={14} className="animate-spin" /> : (isNameDirty ? <Save size={14} /> : <Check size={14} />)}
                    </button>
                  </div>
                </td>
                <td className="p-4 text-sm text-right">
                  <div className="text-green-600 font-bold">${item.estimated_value?.toLocaleString()}</div>
                  <PriceDistributionView
                    rangeValue={item?.estimated_value_range}
                    distributionValue={item?.price_estimate_distribution}
                    maxBins={3}
                    compact
                    className="mt-1 text-left"
                  />
                </td>
                <td className="p-4 text-sm text-indigo-700 font-semibold">{formatConfidencePercent(item?.confidence_score)}</td>
                <td className="p-4 text-sm">
                  <div className="relative inline-flex items-center">
                    <span className="absolute left-2 text-slate-400 text-sm">$</span>
                    <input
                      type="number"
                      placeholder="0.00"
                      value={item.actual_price}
                      onChange={(e) => onPriceChange(item.id, e.target.value)}
                      className={`w-24 pl-5 pr-8 py-1 border rounded text-sm focus:outline-none focus:ring-1 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                        isDirty ? 'border-blue-300 focus:ring-blue-500 bg-blue-50/30' : 'border-slate-200 focus:ring-green-500'
                      }`}
                      step="0.01"
                    />
                    <button
                      onClick={() => onUpdatePrice(item.id, item.actual_price)}
                      disabled={!isDirty || isUpdating}
                      className={`absolute right-1 p-1 rounded transition-all ${
                        isUpdating ? 'text-slate-400' : (isDirty ? 'text-blue-600 hover:bg-blue-100' : 'text-green-500 cursor-default')
                      }`}
                      title={isDirty ? 'Save Price' : 'Price Saved'}
                    >
                      {isUpdating ? <RefreshCcw size={14} className="animate-spin" /> : (isDirty ? <Save size={14} /> : <Check size={14} />)}
                    </button>
                  </div>
                </td>
                <td className="p-4">
                  <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${conditionBadgeClass(condition)}`}>
                    {condition}
                  </span>
                </td>
                <td className="p-4">
                  <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${source.className}`}>
                    {source.label}
                  </span>
                </td>
              </tr>
            );
          })}

          {group.items.length >= 1 && (
            <tr className="bg-slate-50 border-t border-slate-200">
              <td colSpan="2" className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Est. Sum</td>
              <td className="p-4 text-sm text-green-600 font-bold text-right">
                ${group.items.reduce((sum, i) => sum + (parseFloat(i.estimated_value) || 0), 0).toLocaleString()}
              </td>
              <td colSpan="4"></td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default HistoryGroupDesktopTable;
